import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { yupResolver } from "@hookform/resolvers/yup";
import { Controller, useForm } from "react-hook-form";
import * as yup from "yup";
import { ResetPasswordPropsInterface } from "../authTypes";
import React, { useEffect, useMemo, useRef, useState } from "react";
import { resetPasswordRequest, validatePasswordResetTokenRequest } from "../authApi";
import { Col, Form, Row } from "react-bootstrap";
import { toast } from "react-toastify";

export const ResetPassword = () => {
  const { t } = useTranslation();
  const { token } = useParams<{ token: string }>();
  const dispatch = useAppDispatch();
  const { status } = useAppSelector((state) => state.auth);
  const navigate = useNavigate();
  const [isValidToken, setIsValidToken] = useState<boolean | null>(null);
  const [rateLimitError, setRateLimitError] = useState<string | null>(null);
  const hasValidated = useRef(false);

  useEffect(() => {
    if (token && !hasValidated.current) {
      hasValidated.current = true;
      dispatch(validatePasswordResetTokenRequest(token))
        .unwrap()
        .then(() => {
          setIsValidToken(true);
        })
        .catch(() => {
          setIsValidToken(false);
          toast.error(t("password_reset_token_invalid"));
        });
    }
  }, [token, dispatch, t]);

  useEffect(() => {
    if (isValidToken === false) {
      const timer = setTimeout(() => {
        navigate("/login");
      }, 3000);
      return () => clearTimeout(timer);
    }
  }, [isValidToken, navigate]);

  const schema: yup.Schema<ResetPasswordPropsInterface> = useMemo(() => {
    return yup.object({
      password: yup.string().required(t("validations.field_is_required")),
      passwordConfirmation: yup
        .string()
        .required(t("validations.field_is_required"))
        .oneOf(
          [yup.ref("password"), ""],
          t("validations.passwords_must_match")
        ),
    });
  }, [t]);

  const {
    control,
    handleSubmit,
    formState: { errors },
  } = useForm<ResetPasswordPropsInterface>({
    resolver: yupResolver(schema),
    defaultValues: {
      password: "",
      passwordConfirmation: "",
    },
  });

  const onSubmit = (data: ResetPasswordPropsInterface) => {
    setRateLimitError(null);
    dispatch(
      resetPasswordRequest({
        token: token!,
        ...data,
      })
    )
      .unwrap()
      .then(() => {
        toast.success(t("password_reset_success"));
        setTimeout(() => {
          navigate("/login");
        }, 2000);
      })
      .catch((error: any) => {
        // Obsługa Rate Limit (HTTP 429)
        if (error?.status === 429) {
          const retryAfter = error?.retryAfter;
          let errorMessage = t("rate_limit_exceeded");

          if (retryAfter) {
            // Jeśli Retry-After to liczba sekund
            const seconds = parseInt(retryAfter);
            if (!isNaN(seconds)) {
              if (seconds >= 60) {
                const minutes = Math.ceil(seconds / 60);
                errorMessage = t("rate_limit_exceeded_with_minutes", { minutes });
              } else {
                errorMessage = t("rate_limit_exceeded_with_seconds", { seconds });
              }
            }
          }

          setRateLimitError(errorMessage);
          toast.error(errorMessage);
        } else if (error?.status === 400) {
          // Token wygasł lub jest nieprawidłowy
          setIsValidToken(false);
          toast.error(t("password_reset_token_invalid"));
        } else {
          toast.error(t("password_reset_failed"));
        }
      });
  };

  if (isValidToken === null) {
    return (
      <React.Fragment>
        <div className="d-flex align-content-center flex-wrap justify-content-center vh-100">
          <div className="sign-in-form" style={{ width: "600px" }}>
            <h1 className="mb-0">{t("reset_password")}</h1>
            <p>{t("validating_token")}</p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  if (isValidToken === false) {
    return (
      <React.Fragment>
        <div className="d-flex align-content-center flex-wrap justify-content-center vh-100">
          <div className="sign-in-form" style={{ width: "600px" }}>
            <h1 className="mb-0">{t("reset_password")}</h1>
            <p>{t("password_reset_token_invalid")}</p>
          </div>
        </div>
      </React.Fragment>
    );
  }

  return (
    <React.Fragment>
      <div className="d-flex align-content-center flex-wrap justify-content-center vh-100">
        <div className="sign-in-form" style={{ width: "600px" }}>
          <h1 className="mb-0">{t("reset_password")}</h1>
          <p>{t("reset_password_message")}</p>
          {rateLimitError && (
            <div className="alert alert-danger mt-3" role="alert">
              {rateLimitError}
            </div>
          )}
          <form onSubmit={handleSubmit(onSubmit)} className="mt-4">
            <Row className="mb-4">
              <Form.Group as={Col}>
                <Form.Label>{t("password")}</Form.Label>
                <Controller
                  name="password"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      type="password"
                      placeholder={t("placeholders.enter_password")}
                      {...field}
                      isInvalid={!!errors.password}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.password?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row className="mb-4">
              <Form.Group as={Col}>
                <Form.Label>{t("password_confirmation")}</Form.Label>
                <Controller
                  name="passwordConfirmation"
                  control={control}
                  render={({ field }) => (
                    <Form.Control
                      type="password"
                      placeholder={t(
                        "placeholders.enter_password_confirmation"
                      )}
                      {...field}
                      isInvalid={!!errors.passwordConfirmation}
                    />
                  )}
                />
                <Form.Control.Feedback type="invalid">
                  {errors.passwordConfirmation?.message}
                </Form.Control.Feedback>
              </Form.Group>
            </Row>
            <Row>
              <Col>
                <div className="d-inline-block w-100">
                  <button type="submit" className="btn btn-primary float-right">
                    {t("reset_password_button")}
                  </button>
                </div>
              </Col>
            </Row>
          </form>
        </div>
      </div>
    </React.Fragment>
  );
};
